import { FC, useEffect, useMemo } from 'react';

import { getSeasonConfig } from './get-season-config';
import { useSeasonalTheme } from './seasonal-theme-context';
import { SeasonalSemanticColors } from './types';

/**
 * Generates CSS variable overrides from semantic color configuration
 *
 * Since the seasonal theme enables Chakra's dark mode, we only need to override
 * the specific colors that differ from default dark mode (primarily the primary/accent colors
 * and some background tweaks for the winter aesthetic).
 *
 * We intentionally DO NOT override:
 * - Gray scale (dark mode handles this correctly)
 * - White/black swaps (these cause issues with many components)
 * - Most text colors (dark mode defaults are fine)
 */
const generateSeasonalCSS = (themeClassName: string, colors: SeasonalSemanticColors): string => {
	const { primary, bg, text, border } = colors;

	return `
		/* Seasonal Theme - Accent color overrides on top of dark mode */
		.${themeClassName} {
			/* Primary/accent colors - the main seasonal branding */
			--chakra-colors-primary-fg: ${primary.fg} !important;
			--chakra-colors-primary-fgStrong: ${primary.fgStrong} !important;
			--chakra-colors-primary-fgStronger: ${primary.fgStronger} !important;
			--chakra-colors-primary-fgStrongest: ${primary.fgStrongest} !important;
			--chakra-colors-primary-bg: ${primary.bg} !important;
			--chakra-colors-primary-bgHover: ${primary.bgHover} !important;
			--chakra-colors-primary-bgActive: ${primary.bgActive} !important;
			--chakra-colors-primary-bgAlpha: ${primary.bgAlpha} !important;
			--chakra-colors-primary-bgAlphaHover: ${primary.bgAlphaHover} !important;
			--chakra-colors-primary-bgAlphaActive: ${primary.bgAlphaActive} !important;

			/* Background colors - for the winter aesthetic */
			--chakra-colors-bg-canvas: ${bg.canvas} !important;
			--chakra-colors-bg-surface: ${bg.surface} !important;
			--chakra-colors-bg-soft: ${bg.soft} !important;
			--chakra-colors-bg-subtle: ${bg.subtle} !important;
			--chakra-colors-bg-topNav: ${bg.topNav} !important;
			--chakra-colors-bg-inset: ${bg.inset} !important;

			/* Interactive text color to match primary */
			--chakra-colors-text-interactive: ${text.interactive} !important;

			/* Border colors */
			--chakra-colors-border-default: ${border.default} !important;
			--chakra-colors-border-soft: ${border.soft} !important;

			/* Purple color scale - adjust for dark mode readability */
			--chakra-colors-purple-50: #2e1065 !important;
			--chakra-colors-purple-100: #3b0764 !important;
			--chakra-colors-purple-500: #c084fc !important;
			--chakra-colors-purple-600: #a855f7 !important;
		}

		/*
		 * Note: Most component styling is handled by Chakra's dark mode.
		 * We only override primary/accent colors and backgrounds above.
		 * Gray scale, text colors, and white/black are left to dark mode defaults.
		 */

		/*
		 * Error/Alert components - ensure readable text on colored backgrounds
		 * Many components use hardcoded color="red.700" etc, so we override those specifically
		 */
		.${themeClassName} .chakra-alert[data-status="error"],
		.${themeClassName} .chakra-alert[data-status="error"] *,
		.${themeClassName} [role="alert"],
		.${themeClassName} [role="alert"] * {
			color: #fecaca !important; /* red-200 - light red text for dark backgrounds */
		}

		.${themeClassName} .chakra-alert[data-status="error"] svg,
		.${themeClassName} [role="alert"] svg {
			color: #f87171 !important; /* red-400 - brighter icon */
		}

		.${themeClassName} .chakra-alert[data-status="warning"],
		.${themeClassName} .chakra-alert[data-status="warning"] * {
			color: #fef3c7 !important; /* amber-100 - light amber text */
		}

		.${themeClassName} .chakra-alert[data-status="warning"] svg {
			color: #fbbf24 !important; /* amber-400 */
		}

		.${themeClassName} .chakra-alert[data-status="info"],
		.${themeClassName} .chakra-alert[data-status="info"] * {
			color: #dbeafe !important; /* blue-100 - light blue text */
		}

		.${themeClassName} .chakra-alert[data-status="info"] svg {
			color: #60a5fa !important; /* blue-400 */
		}

		.${themeClassName} .chakra-alert[data-status="success"],
		.${themeClassName} .chakra-alert[data-status="success"] * {
			color: #d1fae5 !important; /* emerald-100 - light green text */
		}

		.${themeClassName} .chakra-alert[data-status="success"] svg {
			color: #34d399 !important; /* emerald-400 */
		}

		/* Purple-themed alerts (e.g., pending feedback requests) */
		.${themeClassName} .chakra-alert[style*="purple"],
		.${themeClassName} .chakra-alert[class*="purple"] {
			background-color: #3b0764 !important; /* purple-950 - dark purple */
		}
		.${themeClassName} .chakra-alert[style*="purple"] *,
		.${themeClassName} .chakra-alert[class*="purple"] * {
			color: #e9d5ff !important; /* purple-200 - light purple text */
		}
		.${themeClassName} .chakra-alert[style*="purple"] svg,
		.${themeClassName} .chakra-alert[class*="purple"] svg {
			color: #c084fc !important; /* purple-400 */
		}
		/* Target alerts with inline purple background styles (Chakra applies these as CSS vars) */
		.${themeClassName} .chakra-alert {
			--chakra-colors-purple-100: #3b0764 !important;
			--chakra-colors-purple-50: #3b0764 !important;
		}
		.${themeClassName} .chakra-alert *[color*="purple"] {
			color: #e9d5ff !important;
		}
	`;
};

/**
 * Seasonal Theme Overrides Component
 *
 * When seasonal theme is enabled, this completely overrides all color variables
 * with seasonal-specific values. It doesn't try to manipulate Chakra's light/dark
 * mode - it simply takes full control via CSS !important overrides.
 *
 * Three modes exist:
 * 1. Light mode (Chakra default)
 * 2. Dark mode (Chakra dark)
 * 3. Seasonal mode (complete override, ignores light/dark)
 */
export const SeasonalThemeOverrides: FC = () => {
	const { isSeasonalThemeEnabled } = useSeasonalTheme();
	const seasonConfig = getSeasonConfig();

	// Pick a random button text on mount
	const buttonText = useMemo(() => {
		const randomIndex = Math.floor(Math.random() * seasonConfig.buttonTexts.length);
		return seasonConfig.buttonTexts[randomIndex];
	}, [seasonConfig.buttonTexts]);

	// Add/remove seasonal theme class on root element
	useEffect(() => {
		const root = document.documentElement;

		if (isSeasonalThemeEnabled) {
			root.classList.add(seasonConfig.themeClassName);
		} else {
			root.classList.remove(seasonConfig.themeClassName);
		}

		return () => {
			root.classList.remove(seasonConfig.themeClassName);
		};
	}, [isSeasonalThemeEnabled, seasonConfig.themeClassName]);

	// Replace "Give recognition" button text with seasonal text
	useEffect(() => {
		if (!isSeasonalThemeEnabled) {
			return;
		}

		let foundButton = false;

		const replaceButtonText = () => {
			const giveBoxActions = document.querySelector('[data-testid="give-box-actions"]');
			if (!giveBoxActions) {
				return false;
			}

			const buttons = giveBoxActions.querySelectorAll('button');
			let replacedAny = false;

			buttons.forEach((button) => {
				const walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT, null);

				let node;
				while ((node = walker.nextNode())) {
					if (node.textContent && node.textContent.trim()) {
						const text = node.textContent.trim();
						if (text.includes('Give recognition') || text.includes('Give Recognition') || text === 'Give recognition') {
							node.textContent = node.textContent.replace(/Give recognition/gi, buttonText);
							replacedAny = true;
						}
					}
				}
			});

			return replacedAny;
		};

		if (replaceButtonText()) {
			foundButton = true;
		}

		let intervalId: number | undefined;
		if (!foundButton) {
			intervalId = window.setInterval(() => {
				if (replaceButtonText()) {
					foundButton = true;
					if (intervalId) {
						clearInterval(intervalId);
					}
				}
			}, 200);

			setTimeout(() => {
				if (intervalId) {
					clearInterval(intervalId);
				}
			}, 10000);
		}

		const observer = new MutationObserver(() => {
			replaceButtonText();
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});

		return () => {
			if (intervalId) {
				clearInterval(intervalId);
			}
			observer.disconnect();
		};
	}, [isSeasonalThemeEnabled, buttonText]);

	if (!isSeasonalThemeEnabled) {
		return null;
	}

	const { themeClassName, semanticColors } = seasonConfig;

	return <style>{generateSeasonalCSS(themeClassName, semanticColors)}</style>;
};
